import React, { memo } from 'react';

import { cn } from 'shadcn/lib/utils';

/**
 * Milestone definitions in display order.
 * Each milestone has a key (for logic), a label (for display),
 * and a test function that checks if the milestone is reached.
 */
const MILESTONES = [
    {
        key: 'labels',
        label: 'Labels',
        isReached: (route) => route.labelsCount > 0,
    },
    {
        key: 'packed',
        label: 'Packed',
        isReached: (route) => route.packedCount > 0,
    },
    {
        key: 'transit',
        label: 'Transit',
        isReached: (route) => route.transitCount > 0,
    },
    {
        key: 'complete',
        label: 'Complete',
        isReached: (route) => route.totalShipments > 0 && route.transitCount >= route.totalShipments,
    },
];

/**
 * MilestoneTracker
 *
 * Visual stepper showing progress through Labels → Packed → Transit → Complete.
 * Each step renders as a circle (filled if reached, outlined if not) connected
 * by lines (colored if the next milestone is also reached).
 *
 * @param {Object} props
 * @param {Object} props.route - Route object with labelsCount, packedCount, transitCount, totalShipments
 * @param {string} [props.className] - Additional CSS classes
 */
function MilestoneTracker({ className, route }) {
    if (!route) {
        return null;
    }

    return (
        <div className={cn('flex items-center w-full py-3', className)} data-testid="milestone-tracker">
            {MILESTONES.map((milestone, index) => {
                const reached = milestone.isReached(route);
                const isLast = index === MILESTONES.length - 1;

                // The connector line is "active" if both this milestone and the next are reached
                const nextReached = !isLast && MILESTONES[index + 1].isReached(route);

                return (
                    <React.Fragment key={milestone.key}>
                        {/* Step circle + label */}
                        <div className="flex flex-col items-center gap-1">
                            <div
                                className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors',
                                    reached
                                        ? 'bg-primary border-primary text-primary-foreground'
                                        : 'bg-background border-muted-foreground/30 text-muted-foreground',
                                )}
                                data-testid={`milestone-${milestone.key}`}
                                data-reached={reached}
                            >
                                {reached ? (
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={3}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <span className="text-xs font-medium">{index + 1}</span>
                                )}
                            </div>
                            <span
                                className={cn(
                                    'text-xs font-medium',
                                    reached ? 'text-foreground' : 'text-muted-foreground',
                                )}
                            >
                                {milestone.label}
                            </span>
                        </div>

                        {/* Connector line (not after the last step) */}
                        {!isLast && (
                            <div
                                className={cn(
                                    'flex-1 h-0.5 mx-1 mt-[-1rem] transition-colors',
                                    reached && nextReached ? 'bg-primary' : 'bg-muted-foreground/20',
                                )}
                                data-testid={`connector-${milestone.key}`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default memo(MilestoneTracker);
